import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { translations, STORAGE_KEY } from '../i18n/translations';

const LanguageContext = createContext({
  locale: 'en',
  setLocale: () => {},
  t: (key) => key,
});

export const LanguageProvider = ({ children }) => {
  const [locale, setLocaleState] = useState(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY) || localStorage.getItem(STORAGE_KEY);
      if (saved === 'de' || saved === 'en') return saved;
    } catch {
      /* ignore */
    }
    return 'en';
  });

  const setLocale = useCallback((next) => {
    const v = next === 'de' ? 'de' : 'en';
    setLocaleState(v);
    try {
      sessionStorage.setItem(STORAGE_KEY, v);
      localStorage.setItem(STORAGE_KEY, v);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale === 'de' ? 'de' : 'en';
    }
  }, [locale]);

  const t = useCallback(
    (key) => {
      const table = translations[locale] || translations.en;
      const fallback = translations.en[key];
      return table[key] ?? fallback ?? key;
    },
    [locale]
  );

  const value = useMemo(
    () => ({ locale, setLocale, t }),
    [locale, setLocale, t]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);

/** Pick localized field from API objects (e.g. title_EN / title_DE). */
export function pickLocalized(locale, obj, enKey, deKey) {
  if (!obj || typeof obj !== 'object') return '';
  if (locale === 'de') {
    const de = obj[deKey];
    if (de != null && String(de).trim() !== '') return String(de);
  }
  const en = obj[enKey];
  if (en != null && String(en).trim() !== '') return String(en);
  const de = obj[deKey];
  return de != null ? String(de) : '';
}
